/**
 * THERE IS NO SIGN-IN SCREEN HERE (ruled 2026-08-25).
 *
 * A record's subdomain never asks who somebody is. They answered that
 * at the front door -- typed their work email, which is what chose this
 * subdomain -- so arriving either signs them in or sends them back.
 * Failing to get in means not getting past polywog.io.
 *
 * So this renders no button, no error, and no way to retry: a failed
 * trip returns to the apex, where the question belongs. That also
 * disposes of the redirect loop this path invites, without a guard --
 * a failure LEAVES rather than arriving again.
 *
 * THE ONE THING THAT STAYS is the two-factor challenge, and it is not
 * a sign-in screen. The person has already been identified; this is
 * them proving it is them, against a challenge bound to the attempt in
 * flight. It cannot happen anywhere else -- bouncing to the apex would
 * abandon the very attempt it belongs to.
 */
import { useEffect, useState } from 'react';
import { Button, Card, Input, Space, Typography } from 'antd';
import {
  completeMfaSignIn,
  completeRedirectSignIn,
  mfaChallengeFromError,
  signInWithGoogle,
  type MfaChallenge,
} from '../session';
import { apexUrl, invitedEmail } from '../host';
import { Brand } from './Brand';

export function SignIn() {
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  // A failure is either the 2FA challenge -- which belongs HERE, bound
  // to the attempt in flight -- or a real refusal, which belongs at the
  // front door. Nothing is shown for the second kind: the person goes
  // back and the apex owns the conversation.
  const surface = (err: unknown) => {
    const mfa = mfaChallengeFromError(err);
    if (mfa) {
      setChallenge(mfa);
      return;
    }
    window.location.assign(apexUrl(window.location));
  };

  // NOBODY SHOULD HAVE TO PRESS ANYTHING HERE. They already said who
  // they are at the front door -- typed their work email, which is what
  // chose this subdomain. Asking again is the same question twice.
  //
  // So arriving signed-out STARTS the round trip. Google already holds
  // the consent (one client for the fleet), so for anyone who has been
  // here before it is a bounce they never see; a first-timer gets
  // Google's account chooser, which is the one screen that genuinely
  // needs them.
  //
  // A RECORD SIGNS IN THE PERSON THE APEX IDENTIFIED, and nobody else.
  // The address the front door sent is the whole warrant for being
  // here: it named this org, and it is what Google is told to sign in.
  //
  // WITHOUT IT, THEY DID NOT COME THROUGH THE FRONT DOOR -- they typed
  // the subdomain, followed a stale link, or came back on a trip that
  // carried nothing. All three want the same answer, and it is not a
  // Google account chooser: it is polywog.io, where the question of who
  // they are gets asked.
  //
  // That also disposes of the back button. Pressing back at Google
  // returns here without the fragment, which reads as "did not come
  // through the front door" rather than as a fresh arrival -- so the
  // trip is not started again and nobody is trapped between two pages.
  useEffect(() => {
    void completeRedirectSignIn().then((err) => {
      if (err) {
        surface(err);
        return;
      }
      const email = invitedEmail(window.location);
      if (email === null) {
        window.location.assign(apexUrl(window.location));
        return;
      }
      void run(() => signInWithGoogle(email));
    });
  }, []);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
    } catch (err) {
      surface(err);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    void run(() => completeMfaSignIn(challenge, mfaCode.trim()));
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '2rem' }}>
      <Space orientation="vertical" size="large" style={{ width: '100%', maxWidth: 380 }}>
        <Brand />
        <Card size="small">
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            {challenge ? (
              <>
                <Typography.Text type="secondary">
                  Enter the 6-digit code from your authenticator app.
                </Typography.Text>
                <form onSubmit={submitMfa}>
                  <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                    <Input
                      inputMode="numeric"
                      pattern="[0-9]*"
                      required
                      autoFocus
                      size="large"
                      value={mfaCode}
                      onChange={(e) => setMfaCode(e.target.value)}
                      placeholder="123456"
                      aria-label="Authentication code"
                    />
                    <Button type="primary" htmlType="submit" loading={busy} block size="large">
                      Verify
                    </Button>
                  </Space>
                </form>
              </>
            ) : (
              // The trip, in flight. Not a screen -- a transition. There
              // is nothing to decide and nothing to press.
              <Typography.Text type="secondary">Signing you in\u2026</Typography.Text>
            )}
          </Space>
        </Card>
      </Space>
    </div>
  );
}
